import { formatDateTime } from '@/components/BatchList';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { execute } from '@/graphql/execute';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageSkeleton from '@/layout/Skeleton';
import { createReferralMutation, deleteReferralMutation } from '@/mutations/referral.mutation';
import { useDialog } from '@/providers/DialogProvider';
import { myReferralsQuery } from '@/queries/referral.query';
import { UserPlusSolid } from '@mynaui/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { AlertTriangle, Check, Circle, Loader2, Search, Trash2 } from 'lucide-react';
import { analytics } from '@/lib/analytics';
import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export default function ReferralsPage() {
  const { t } = useTranslation('auth');
  usePageMeta({ title: [t('referral.title')] });
  const { openDialog } = useDialog();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['myReferrals'],
    queryFn: () => execute(myReferralsQuery),
  });

  const referrals = data?.myReferrals ?? [];
  const filteredReferrals = referrals.filter(
    (r) =>
      r.referredEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.referredUser?.name?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleDelete = async (id: string) => {
    try {
      await execute(deleteReferralMutation, { id });
      analytics.capture('Referral Deleted');
      toast.success(t('referral.referralDeleted'));
      queryClient.invalidateQueries({ queryKey: ['myReferrals'] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete';
      toast.error(msg);
    }
  };

  if (isLoading) {
    return (
      <PageSkeleton steps={[{ label: t('referral.title'), href: '/referrals' }]}>
        <div className='flex items-center justify-center h-64'>
          <div className='flex flex-col items-center justify-center'>
            <Loader2 className='size-8 animate-spin' />
          </div>
        </div>
      </PageSkeleton>
    );
  }

  return (
    <PageSkeleton steps={[{ label: t('referral.title'), href: '/referrals' }]}>
      <div className='max-w-screen-md mx-auto px-4 pt-6 w-full flex flex-col gap-4'>
        <div className='flex items-center justify-between mb-4'>
          <div className='relative flex-1 max-w-sm'>
            <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
            <Input
              placeholder={t('referral.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className='pl-8 bg-white'
            />
          </div>
          <Button
            onClick={() =>
              openDialog(
                <CreateReferralDialog
                  onSuccess={() => queryClient.invalidateQueries({ queryKey: ['myReferrals'] })}
                />,
              )
            }>
            <UserPlusSolid className='size-4' />
            {t('referral.newReferral')}
          </Button>
        </div>

        <div className='rounded-md border bg-white dark:bg-stone-900 dark:border-stone-800'>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('referral.columnEmail')}</TableHead>
                <TableHead>{t('referral.columnStatus')}</TableHead>
                <TableHead>{t('referral.columnDate')}</TableHead>
                <TableHead>{t('referral.columnExpires')}</TableHead>
                <TableHead className='w-[50px]'></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredReferrals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className='text-center py-8 text-muted-foreground'>
                    {searchTerm ? t('referral.noReferralsSearch') : t('referral.noReferrals')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredReferrals.map((referral) => (
                  <TableRow key={referral.id}>
                    <TableCell>
                      <div>
                        <div className='font-medium'>
                          {referral.referredUser?.name || referral.referredEmail}
                        </div>
                        {referral.referredUser && (
                          <div className='text-xs text-muted-foreground'>{referral.referredEmail}</div>
                        )}
                        {referral.organization && (
                          <div className='text-xs text-muted-foreground'>{referral.organization.name}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className='flex items-center gap-2'>
                        <StepBadge done={!!referral.accountCreatedAt} label={t('referral.stepAccountCreated')} />
                        <StepBadge done={!!referral.organizationCreatedAt} label={t('referral.stepOrgCreated')} />
                        <StepBadge done={!!referral.subscriptionStartedAt} label={t('referral.stepSubscribed')} />
                      </div>
                    </TableCell>
                    <TableCell>{formatDateTime(referral.createdAt)}</TableCell>
                    <TableCell>
                      {new Date(referral.expiresAt) < new Date() ? (
                        <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'>
                          <AlertTriangle className='size-3' />
                          {t('referral.expired')}
                        </span>
                      ) : (
                        <span className='text-sm text-muted-foreground'>
                          {formatDateTime(referral.expiresAt)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => handleDelete(referral.id)}
                        className='text-muted-foreground hover:text-red-500 transition-colors p-1'>
                        <Trash2 className='size-3.5' />
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </PageSkeleton>
  );
}

const StepBadge = ({ done, label }: { done: boolean; label: string }) => (
  <span
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
      done
        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
        : 'bg-slate-100 dark:bg-stone-800 text-slate-400 dark:text-stone-500'
    }`}>
    {done ? <Check className='size-3' /> : <Circle className='size-3' />}
    {label}
  </span>
);

const CreateReferralDialog: FunctionComponent<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const { t } = useTranslation('auth');
  const { dismissActiveDialog } = useDialog();

  const formik = useFormik({
    initialValues: { email: '', message: '' },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.email.trim()) {
        errors.email = t('referral.emailRequired');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
        errors.email = t('referral.invalidEmail');
      }
      return errors;
    },
    onSubmit: async (values) => {
      const toastId = toast.loading(t('referral.sending'));
      try {
        await execute(createReferralMutation, { email: values.email.trim(), message: values.message.trim() || undefined });
        analytics.capture('Referral Created');
        toast.success(t('referral.inviteSent'), { id: toastId });
        onSuccess();
        dismissActiveDialog();
      } catch (err) {
        const msg = err instanceof Error ? err.message : t('referral.sendFailed');
        toast.error(msg, { id: toastId });
      }
    },
  });

  return (
    <div className='space-y-4'>
      <div>
        <h3 className='text-lg font-semibold'>{t('referral.newReferral')}</h3>
        <p className='text-sm text-muted-foreground'>{t('referral.newReferralDescription')}</p>
      </div>
      <form onSubmit={formik.handleSubmit} className='space-y-4'>
        <div className='space-y-2'>
          <Label htmlFor='email'>{t('referral.emailLabel')}</Label>
          <Input
            id='email'
            name='email'
            type='email'
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={t('referral.emailPlaceholder')}
          />
          {formik.touched.email && formik.errors.email && (
            <p className='text-sm text-red-500'>{formik.errors.email}</p>
          )}
        </div>
        <div className='space-y-2'>
          <Label htmlFor='message'>{t('referral.messageLabel')}</Label>
          <Textarea
            id='message'
            name='message'
            value={formik.values.message}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={t('referral.messagePlaceholder')}
            rows={3}
          />
        </div>
        <div className='flex justify-end gap-2'>
          <Button type='button' variant='outline' onClick={dismissActiveDialog}>
            {t('referral.cancel')}
          </Button>
          <Button type='submit' disabled={formik.isSubmitting}>
            {formik.isSubmitting ? <Loader2 className='size-4 animate-spin mr-1' /> : null}
            {t('referral.sendInvite')}
          </Button>
        </div>
      </form>
    </div>
  );
};
